/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {loadGeofences} from '@ion/core/src/services/ArcGIS';
import {GenericObject, GeoFenceAttributes} from '@ion/core/src/types/entities';
import {GeofenceQueryParams} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseLoadGeofenceResults = UseDefaultQueryResult & {
  results: GeoFenceAttributes[];
  refresh: Function;
  fetchNextPage: Function;
  hydrateData?: Function;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
};

export interface UseLoadGeofence {
  baseKey: QueryKey;
  enabled?: boolean;
  filterOn?: string;
  rowsPerPage?: number;
  globalGroupId?: number;
  queryParams?: GeofenceQueryParams;
  staleTime?: number | DurationResult;
};

// returns the number of records received across the given pages ...
export const getLoadedCount = (pages: GeoFenceAttributes[][]): number => (
  pages?.reduce((count, page) => count + (page?.length ?? 0), 0) ?? 0
);

// returns the index of the last page holding records, or 0 when none do ...
const getLastLoadedPageIndex = (pages: GeoFenceAttributes[][]): number => (
  pages?.reduce((lastIndex, page, index) => (page?.length ? index : lastIndex), 0) ?? 0
);

// removes the trailing empty page, keeping a lone first page (no geofences) ...
export const removeTrailingEmptyPage = (data: GenericObject): GenericObject => {
  const pages = data?.pages;
  return pages?.length > 1 && !pages[pages.length - 1]?.length
    ? {...data, pages: pages.slice(0, -1), pageParams: data.pageParams?.slice(0, -1)}
    : data;
};

/**
 * The useLoadGeofence hook provides the Query for referencing the current geofence data.
 *
 * ```typescript
 *
 * import {useLoadGeofences} from '@ion/query/src/hooks/useLoadGeofences';
 *
 *  ...
 *
 *  const {data: geofenceResults} = useLoadGeofences();
 *
 * ```
 * 
 * @param   baseKey a constant passed in to identify the dependency key.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to allow for updates to occur only at the user's discretion.
 * @param   queryParams is built through Report filters to be used in the Geofence request.
 * @returns UseLoadGeofenceResults containing the query results and the current Tracking Geofence.
 */
export const useLoadGeofences = ({
  baseKey,
  queryParams,
  globalGroupId,
  rowsPerPage = 10000,
  staleTime = getStaleTime(60, false, false),
  enabled = false
}: UseLoadGeofence): UseLoadGeofenceResults => {
  const queryClient = useQueryClient();

  const keys = useMemo(() => (
    createKey(baseKey, globalGroupId)
  ), [baseKey, globalGroupId]);

  // the page param is the offset of the page's first record ...
  const fetchGeofences = useCallback(async({pageParam: resultOffset = 0}) => {
    const results = await loadGeofences({
      ...queryParams,
      resultOffset,
      isFMTracking      : true,
      resultRecordCount : rowsPerPage
    } as any);
    return results;
  }, [queryParams, rowsPerPage]);

  // the feature service caps each response at its own max record count (which
  // may be lower than rowsPerPage), so a short page doesn't mean the last one -
  // pages are requested until one comes back empty, each starting after the
  // records already received ...
  const query = useInfiniteQuery({
    queryKey         : keys,
    queryFn          : fetchGeofences,
    getNextPageParam : (lastPage, pages) => (
      lastPage?.length ? getLoadedCount(pages) : undefined
    ),
    // drops the trailing empty page, which only marks the end of the records ...
    select    : removeTrailingEmptyPage,
    staleTime : staleTime as number,
    enabled
  } as any);

  // splits the given geofences back into the cached pages, by each page's size - the
  // last loaded page takes any remainder, so the trailing empty page still ends the
  // records ...
  //
  // a group with no loaded pages is left alone - writing to it would create a fresh,
  // empty cache entry, and the group's geofences would never be fetched while it
  // stays fresh ...
  const hydrateData = (data: GeoFenceAttributes[], baseKey: QueryKey, globalGroupId: number) => {
    const keys = createKey(baseKey, globalGroupId);
    if ((queryClient.getQueryData(keys) as GenericObject)?.pages?.length) {
      queryClient.setQueryData(keys, (oldData: GenericObject) => {
        const lastLoadedIndex = getLastLoadedPageIndex(oldData?.pages);
        let start = 0;
        return {
          pages : oldData?.pages?.map((page, index) => {
            const end   = index === lastLoadedIndex ? data?.length : start + (page?.length ?? 0);
            const slice = data?.slice(start, end);
            start       = end;
            return slice;
          }),
          pageParams : oldData?.pageParams
        };
      });
    }
  };
    
  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;

  if (enabled && hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  return createQueryResult(query, {
    results            : data || [],
    isFetchingNextPage : isFetchingNextPage,
    fetchNextPage      : fetchNextPage,
    hasNextPage        : hasNextPage,
    refresh            : refetch,
    hydrateData,
    enabled
  }) as UseLoadGeofenceResults;
};
